<template>
  <div>
    <router-link
      class="link-back"
      :to="`/research/test/${$route.params.id}/insights`"
    >
      <div>&larr; Back to Insights</div>
    </router-link>
    <h1 v-bg-typography="'h1'" style="margin-top: 20px; margin-bottom: 10px">
      Insight Wizard
    </h1>
    <p v-bg-typography="'body-m'">Generate insights using chatGPT.</p>
    <bg-loader :dark="true" v-if="isLoading"></bg-loader>

    <div class="form" v-if="!isLoading">
      <bg-field
        directions="Describe the content of this test so chatGPT better understands the feedback."
        label="Background of the test"
        name="purpose"
      >
        <bg-input
          placeholder="Example: The test revolves around a new device ('pod') that users can install in their car to monitor driving behavior. That way they can receive discount if they drive environmentally conscious."
          tag="textarea"
          v-model="about"
          :rows="3"
        />
      </bg-field>

      <bg-field
        directions="Which focus point is linked to this tag?"
        label="Focus point"
        name="purpose"
      >
        <bg-select
          :options="test.focus_elements"
          placeholder="Focus point"
          :value="focus_point"
          style="margin-bottom: 0"
          @change="selectFocus"
          :extraWide="true"
        ></bg-select>
      </bg-field>

      <bg-field
        directions="For which tag do you want to generate an insight."
        label="Tag"
        name="purpose"
      >
        <bg-select
          :options="tags"
          placeholder="Tag"
          :value="tag"
          @change="selectTag"
          label="name"
          style="margin-bottom: 0"
        ></bg-select>
      </bg-field>

      <bg-field
        v-if="tag"
        directions="Describe the tag in one sentence to chatGPT."
        label="Tag description"
        name="purpose"
      >
        <bg-input
          :placeholder="`${tag.name} is about...`"
          tag="input"
          v-model="tag.description"
          height="46px"
        />
      </bg-field>

      <bg-button
        @click="generate"
        variant="primary"
        text="Generate insight"
        style="margin-bottom: 30px; margin-right: 20px"
      />

      <p
        v-if="errorMessage"
        v-bg-typography="'body-m'"
        style="color: #ff14a9; margin-bottom: 30px"
      >
        {{ errorMessage }}
      </p>
    </div>
  </div>
</template>

<script>
import BgTypography from "@/directives/bg-typography";
import BgLoader from "@/components/bg-loader";
import BgField from "@/components/bg-field";
import BgInput from "@/components/bg-input";
import { mapState } from "vuex";
import BgCheckbox from "@/components/bg-checkbox";
import BgButton from "@/components/bg-button";
import BgSelect from "@/components/bg-select";
import API from "@/api";

export default {
  name: "insight-wizard",
  components: {
    BgButton,
    BgLoader,
    BgSelect,
    BgField,
    BgInput,
    BgCheckbox,
  },
  computed: {
    ...mapState({
      test: (state) => state.test,
    }),
  },
  directives: {
    BgTypography,
  },
  data() {
    return {
      isLoading: false,
      about: "",
      focus_point: null,
      tag: null,
      tags: [],
      page: null,
      errorMessage: null,
    };
  },
  async mounted() {
    this.isLoading = true;
    try {
      const res = await API.test.getTags(this.$route.params.id);
      this.tags = res.tags || [];
    } catch (e) {
      console.error(e);
      this.errorMessage = "Could not load the tags for this test. Please try again.";
    }
    this.isLoading = false;
  },
  methods: {
    async generate() {
      this.errorMessage = null;

      if (!this.tag) {
        this.errorMessage = "Please select a tag before generating an insight.";
        return;
      }

      this.isLoading = true;

      try {
        let data = {
          about: this.about,
          tag: this.tag,
          focus_point: this.focus_point,
        };

        const res = await API.openAi.generateInsight(data);

        const result = JSON.parse(res);
      console.log(result);
      let explanation = "";
      for (let x in result.common_factors) {
        let f = result.common_factors[x];
        explanation = explanation.concat(`<b>${f.factor}</b>`);
        explanation = explanation.concat("<br><br>");
        explanation = explanation.concat(f.user_feedback);
        explanation = explanation.concat("<br><br>");
        explanation = explanation.concat(f.behavior_explanation);
        explanation = explanation.concat("<br><br>");
        explanation = explanation.concat(f.psychological_theory);
        explanation = explanation.concat("<br><br>");
      }
      if (this.focus_point) {
        explanation = explanation.concat("<b>Focus point:</b><br><br>");
        explanation = explanation.concat(result.answer_to_research_question);
      }

      await API.test.createInsight(this.$route.params.id, {
        title: result.title ? result.title : "...",
        behavior_comment: "...",
        eeg_comment: result.main_emotions,
        explanation: explanation,
        eye_tracking_comment: "",
        highlight_id: this.tag.significant_points[0].id,
        significant_points_ids: this.tag.significant_points.map((s) => s.id),
        suggestion: "",
        summary: result.summary,
        touch_tracking_comment: "",
        focus_point: this.focus_point,
      });
      const res2 = await API.test.getInsights(this.$route.params.id);
      const latest = await res2.data.insights.reduce(function (prev, current) {
        return prev.id > current.id ? prev : current;
      });
      for (let x in result.suggestions) {
        await API.test.createSuggestion({
          author_id: this.$store.state.user.user.id,
          title: result.suggestions[x].suggestion_title,
          suggestion: result.suggestions[x].suggestion_description,
          test_id: this.$route.params.id,
          insight_id: latest.id,
        });
      }

      function delay(time) {
        return new Promise((resolve) => setTimeout(resolve, time));
      }

      await delay(1000);
      console.log("ran after 1 second1 passed");

        this.isLoading = false;
        this.$router.push(
          `/research/test/${this.$route.params.id}/insights?open=${latest.id}`,
        );
      } catch (e) {
        console.error(e);
        this.errorMessage =
          "Something went wrong while generating the insight. Please try again.";
        this.isLoading = false;
      }
    },
    selectFocus(t) {
      this.focus_point = t;
    },
    selectTag(t) {
      this.tag = t;
    },
  },
};
</script>

<style lang="scss" scoped>
.form {
  max-width: 1000px;
}
.tags {
  .tag {
    display: grid;
    grid-template-columns: 30px 2fr 2fr;
    gap: 20px;
    align-items: center;
  }
}
</style>
